import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Upload, Loader2, X, FileText, Lock, MapPin } from 'lucide-react';
import { api } from '../../../lib/auth';
import { Sheet, SubmitButton, DoneScreen } from './Sheet';
import { GeoErrorModal } from './GeoErrorModal';
import { useGeo, GeoBox, KmInput, Stepper, TimeInput, BigField, nowHHMM, uploadAttachment } from './parts';
import { CustomFields, initialCustom, missingCustom, cleanAnswers } from './CustomFields';
import { useTranslation, useI18nStore } from '../../../lib/i18n';
const tt = (k, p) => useI18nStore.getState().t(`startStop.${k}`, p);

const err = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join(' · ') : (d || tt('saveFailed'));
};
const DEFAULT_FIELDS = { start_km: { show: false, required: false }, end_km: { show: false, required: false }, attachments: { show: false, required: false } };
const cfgOf = (a) => ({ ...DEFAULT_FIELDS, ...(a.start_stop?.fields || {}) });
const customOf = (a, phase) => (a.start_stop?.custom_fields || []).filter((f) => f.ss_phase === phase);
const lbl = (text, c) => (c.required ? `${text} *` : `${text} ${tt('optional')}`);
const num = (v) => (v === '' || v == null ? null : parseInt(v, 10));
const photoIds = (p) => Object.fromEntries(Object.entries(p).filter(([, v]) => v?.id).map(([k, v]) => [k, v.id]));

function useGeoOnOpen() {
  const [geo, request] = useGeo();
  useEffect(() => { request(); }, [request]);
  return [geo, request];
}

function usePhase(a, phase) {
  const fields = customOf(a, phase);
  const [answers, setAnswers] = useState(() => initialCustom(fields));
  const [photos, setPhotos] = useState({});
  return { fields, answers, setAnswers, photos, setPhotos };
}

export function StartSheet({ a, onClose, onDone }) {
  const { t: tr } = useTranslation();
  const t = (k, p) => tr(`startStop.${k}`, p);
  const cfg = cfgOf(a);
  const cf = usePhase(a, 'start');
  const preset = a.start_stop?.preset || {};
  const [geo, retry] = useGeoOnOpen();
  const [km, setKm] = useState('');
  const [photo, setPhoto] = useState(null);
  const [time, setTime] = useState(preset.time || nowHHMM());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const geoOk = geo.status === 'ok' || !!preset.location;

  const submit = async () => {
    toast.dismiss();
    if (!geoOk) return toast.error(tt('noLocStart'));
    const miss = [...(cfg.start_km.show && cfg.start_km.required && !km && !photo ? [`${cfg.start_km.label || tt('startKm')} ${tt('orPhoto')}`] : []), ...missingCustom(cf.fields, cf.answers, cf.photos)];
    if (miss.length) return toast.error(tt('missing', { list: miss.join(', ') }));
    setBusy(true);
    try {
      await api.post(`/planning/assignments/${a.id}/start`, {
        location: geo.status === 'ok' ? geo.coords : null, km: cfg.start_km.show ? num(km) : null, km_photo_id: cfg.start_km.show ? photo?.id || null : null,
        entered_time: preset.time || time, client_time: nowHHMM(), answers: cleanAnswers(cf.fields, cf.answers), photos: photoIds(cf.photos),
      });
      toast.dismiss();
      setDone(true);
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  if (done) {
    return <DoneScreen testId="ss-start-done" title={tt('startedTitle')} onClose={onDone}
      lines={[tt('goodWork'), tt('canClose')]} />;
  }
  return (
    <Sheet testId="ss-start-sheet" title={t('startTitle')} subtitle={`${a.route_code || ''} · ${a.depot_name || ''}`} onClose={onClose}
      footer={<SubmitButton onClick={submit} busy={busy} disabled={!geoOk} testId="ss-start-submit">START</SubmitButton>}>
      {preset.location ? (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800" data-testid="ss-preset-location">
          <MapPin className="h-5 w-5 shrink-0" /><span>{t('presetLocation', { depot: preset.location_label || a.depot_name || '' })}</span>
        </div>
      ) : (
        <>
          <GeoBox geo={geo} onRetry={retry} />
          <GeoErrorModal geo={geo} onRetry={retry} />
        </>
      )}
      {cfg.start_km.show && (
        <BigField label={lbl(cfg.start_km.label || t('startKm'), cfg.start_km)} hint={cfg.start_km.help || t('kmHintStart')}>
          <KmInput assignmentId={a.id} value={km} onChange={setKm} photo={photo} onPhoto={setPhoto} testId="ss-start-km" />
        </BigField>
      )}
      {preset.time ? (
        <BigField label={t('startTime')} hint={t('presetTimeHint')}>
          <div className="h-14 rounded-xl border-2 border-slate-200 bg-slate-100 dark:bg-slate-800 dark:border-slate-700 px-4 flex items-center justify-between text-2xl font-bold tabular-nums text-slate-700 dark:text-slate-200" data-testid="ss-start-time-locked">
            {preset.time}<Lock className="h-5 w-5 text-slate-400" />
          </div>
        </BigField>
      ) : (
        <BigField label={t('startTime')} hint={t('timeHint')}><TimeInput value={time} onChange={setTime} testId="ss-start-time" /></BigField>
      )}
      <CustomFields {...cf} assignmentId={a.id} prefix="ss-start" />
    </Sheet>
  );
}

function ExtraFiles({ assignmentId, items, setItems, label }) {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of files) {
      const tmp = { tmp: Math.random().toString(36).slice(2), file_name: f.name || 'foto.jpg', busy: true };
      setItems((p) => [...p, tmp]);
      try {
        const d = await uploadAttachment(assignmentId, f);
        setItems((p) => p.map((x) => (x.tmp === tmp.tmp ? { ...d } : x)));
      } catch (er) {
        setItems((p) => p.filter((x) => x.tmp !== tmp.tmp));
        toast.error(err(er));
      }
    }
  };
  return (
    <BigField label={label}>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => camRef.current?.click()} className="h-12 rounded-xl border-2 border-dashed border-emerald-400 text-emerald-700 font-semibold flex items-center justify-center gap-2" data-testid="ss-stop-extra-camera"><Camera className="h-5 w-5" />{tt('takePhoto')}</button>
        <button type="button" onClick={() => fileRef.current?.click()} className="h-12 rounded-xl border-2 border-dashed border-sky-400 text-sky-700 font-semibold flex items-center justify-center gap-2" data-testid="ss-stop-extra-file"><Upload className="h-5 w-5" />{tt('addFile')}</button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={add} />
      <input ref={fileRef} type="file" multiple className="hidden" onChange={add} />
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {items.map((it) => (
            <div key={it.id || it.tmp} className="relative h-16 w-16 rounded-lg border overflow-hidden bg-slate-100 flex items-center justify-center">
              {it.busy ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                : (it.content_type || '').startsWith('image/') && it.cdn_url ? <img src={it.cdn_url} alt="" className="h-full w-full object-cover" />
                  : <FileText className="h-6 w-6 text-slate-400" />}
              {!it.busy && <button type="button" onClick={() => setItems((p) => p.filter((x) => x.id !== it.id))} className="absolute top-0.5 right-0.5 rounded-full bg-rose-600 text-white p-0.5"><X className="h-3 w-3" /></button>}
            </div>
          ))}
        </div>
      )}
    </BigField>
  );
}

export function StopSheet({ a, rec, onClose, onDone }) {
  const { t: tr } = useTranslation();
  const t = (k, p) => tr(`startStop.${k}`, p);
  const cfg = cfgOf(a);
  const cf = usePhase(a, 'stop');
  const [geo, retry] = useGeoOnOpen();
  const [km, setKm] = useState('');
  const [photo, setPhoto] = useState(null);
  const [time, setTime] = useState(nowHHMM());
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const startKm = rec?.start?.km;

  const submit = async () => {
    toast.dismiss();
    if (geo.status !== 'ok') return toast.error(tt('noLocStop'));
    const miss = [
      ...(cfg.end_km.show && cfg.end_km.required && !km && !photo ? [`${cfg.end_km.label || tt('endKm')} ${tt('orPhoto')}`] : []),
      ...(cfg.attachments.show && cfg.attachments.required && !files.length ? [tt('attachments')] : []),
      ...missingCustom(cf.fields, cf.answers, cf.photos),
    ];
    if (miss.length) return toast.error(tt('missing', { list: miss.join(', ') }));
    if (km && startKm != null && parseInt(km, 10) < startKm) return toast.error(tt('endKmLess', { km: startKm }));
    if (files.some((f) => f.busy)) return toast.error(tt('filesUploading'));
    setBusy(true);
    try {
      const r = await api.post(`/planning/assignments/${a.id}/stop`, {
        location: geo.coords, km: num(km), km_photo_id: photo?.id || null, entered_time: time, client_time: nowHHMM(),
        attachment_ids: files.map((f) => f.id).filter(Boolean), answers: cleanAnswers(cf.fields, cf.answers), photos: photoIds(cf.photos),
      });
      toast.dismiss();
      setDone(r.data?.data);
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  if (done) {
    const total = done.stop?.km != null && done.start?.km != null ? done.stop.km - done.start.km : null;
    return <DoneScreen testId="ss-stop-done" title={tt('finished')} onClose={onDone}
      lines={[tt('savedThanks'), total != null ? tt('totalKm', { km: total }) : tt('recordReceived'), tt('oneCorrection')]} />;
  }
  return (
    <Sheet testId="ss-stop-sheet" tone="rose" title={t('stopTitle')} subtitle={`Start: ${rec?.start?.entered_time || '—'}${startKm != null ? ` · ${startKm} km` : ''}`} onClose={onClose}
      footer={<SubmitButton tone="rose" onClick={submit} busy={busy} disabled={geo.status !== 'ok'} testId="ss-stop-submit">{t('stopSave')}</SubmitButton>}>
      <GeoBox geo={geo} onRetry={retry} />
      <GeoErrorModal geo={geo} onRetry={retry} />
      {cfg.end_km.show && (
        <BigField label={lbl(cfg.end_km.label || t('endKm'), cfg.end_km)} hint={cfg.end_km.help || t('kmHintStop')}>
          <KmInput assignmentId={a.id} value={km} onChange={setKm} photo={photo} onPhoto={setPhoto} testId="ss-stop-km" />
        </BigField>
      )}
      <BigField label={t('endTime')} hint={t('timeHint')}><TimeInput value={time} onChange={setTime} testId="ss-stop-time" /></BigField>
      <CustomFields {...cf} assignmentId={a.id} prefix="ss-stop" />
      {cfg.attachments.show && <ExtraFiles assignmentId={a.id} items={files} setItems={setFiles} label={lbl(t('attachmentsPhotos'), cfg.attachments)} />}
    </Sheet>
  );
}

export function CorrectionSheet({ a, rec, onClose, onDone }) {
  const { t: tr } = useTranslation();
  const t = (k, p) => tr(`startStop.${k}`, p);
  const cfg = cfgOf(a);
  const s = rec.stop || {};
  const numFields = customOf(a, 'stop').filter((f) => f.type === 'number' && s.answers && s.answers[f.key] != null);
  const initKm = s.km != null ? String(s.km) : '';
  const initAns = Object.fromEntries(numFields.map((f) => [f.key, String(s.answers[f.key])]));
  const [km, setKm] = useState(initKm);
  const [ans, setAns] = useState(initAns);
  const [time, setTime] = useState(s.entered_time || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    toast.dismiss();
    if (reason.trim().length < 3) return toast.error(tt('reasonRequired'));
    const body = { reason };
    if (km !== initKm && km !== '') body.km = parseInt(km, 10);
    if (time !== (s.entered_time || '')) body.entered_time = time;
    const changed = Object.fromEntries(numFields.filter((f) => ans[f.key] !== initAns[f.key] && ans[f.key] !== '').map((f) => [f.key, Number(ans[f.key])]));
    if (Object.keys(changed).length) body.answers = changed;
    if (Object.keys(body).length === 1) return toast.info(tt('noChange'));
    setBusy(true);
    try { await api.post(`/planning/assignments/${a.id}/start-stop/correct`, body); toast.success(tt('correctionSaved')); onDone(); }
    catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };
  return (
    <Sheet testId="ss-correct-sheet" tone="amber" title={t('correctTitle')} subtitle={t('correctSubtitle')} onClose={onClose}
      footer={<SubmitButton tone="amber" onClick={submit} busy={busy} testId="ss-correct-submit">{t('saveCorrection')}</SubmitButton>}>
      {cfg.end_km.show && <BigField label={t('endKm')}><Stepper value={km} onChange={setKm} testId="ss-correct-km" maxDigits={7} /></BigField>}
      <BigField label={t('endTime')}><TimeInput value={time} onChange={setTime} testId="ss-correct-time" /></BigField>
      {numFields.map((f) => (
        <BigField key={f.key} label={f.label}>
          <Stepper value={ans[f.key]} onChange={(v) => setAns((p) => ({ ...p, [f.key]: v }))} testId={`ss-correct-cf-${f.key}`} maxDigits={6} />
        </BigField>
      ))}
      <BigField label={t('correctionReason')}>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-3 text-base" placeholder={t('reasonPlaceholder')} data-testid="ss-correct-reason" />
      </BigField>
    </Sheet>
  );
}
